/* ==========================================================================
   Bossi layer: behaviours shared by the Bossi sections.
   - Reveal: [data-b-reveal] and [data-b-stagger] come into view as the page scrolls.
   - Figures: [data-b-count] counts up to its number when it comes into view ("37 hours").
   - Galleries: [data-b-rail] sideways rows with arrows, drag on a computer, progress line.
   - Feature story: [data-b-story] swaps the pinned image as each feature is read.
   - In-page bar: [data-b-subnav] highlights where the reader is on a model page; on a phone (or
     wherever its links do not fit) it becomes a button that drops down the list of parts.
   - Questions: details.b-acc__item open and close smoothly; one opened for computers starts
     closed on phones.
   - Shortened text: [data-b-clamp] shows a few lines and Read more on phones.
   - Video: [data-b-video] loads the player only when asked for.
   - List of links: [data-b-index] shows each link's photo beside the pointer on a computer.
   - Builder links: "Build yours" becomes "Continue your build" once a build is started, and the
     bike page's opening photo glides into the builder.
   - Recent builds: [data-b-builds] filters the library by bike (and reads ?bike= from the
     address); [data-b-post-gallery] swipes through a build's photos, with thumbnails and arrows.
   - Enquiry forms: first and last name are also sent together as "Name".
   - Page position: stops a jump to the end of a page (after a form) sliding it over blank space.
   Everything respects "reduced motion". Re-runs when a section is edited in the theme editor.
   ========================================================================== */

const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
const phone = window.matchMedia('(max-width: 749px)');
const root = document.documentElement;
const motion = () => root.classList.contains('b-motion') && !reduce.matches;

/* ---------- Reveal ---------- */

let revealObserver;
function initReveal(scope) {
  const targets = scope.querySelectorAll('[data-b-reveal]:not(.is-in), [data-b-stagger]:not(.is-in)');
  scope.querySelectorAll('[data-b-stagger]').forEach((group) => {
    [...group.children].forEach((child, i) => child.style.setProperty('--b-i', String(i)));
  });
  if (!targets.length) return;
  if (!motion() || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-in'));
    return;
  }
  revealObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        revealObserver.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
  );
  targets.forEach((el) => revealObserver.observe(el));
}

/* ---------- Counting figures ---------- */

function initCounts(scope) {
  const items = scope.querySelectorAll('[data-b-count]:not([data-b-counted])');
  if (!items.length) return;
  const run = (el) => {
    el.dataset.bCounted = '1';
    const text = el.textContent;
    const match = text.match(/^(\D*)(\d[\d,]*(?:\.\d+)?)(.*)$/s);
    if (!match || !motion()) return;
    const [, before, num, after] = match;
    const target = parseFloat(num.replace(/,/g, ''));
    const decimals = (num.split('.')[1] || '').length;
    const start = performance.now();
    const duration = 1600;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 4);
      const value = (target * eased).toFixed(decimals);
      el.textContent = before + Number(value).toLocaleString('en-AU', { minimumFractionDigits: decimals }) + after;
      if (p < 1) requestAnimationFrame(tick);
      else el.textContent = text;
    };
    requestAnimationFrame(tick);
  };
  if (!('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        io.unobserve(entry.target);
        run(entry.target);
      }
    },
    { threshold: 0.6 }
  );
  items.forEach((el) => io.observe(el));
}

/* ---------- Rails (sideways galleries and card rows) ---------- */

function initRails(scope) {
  scope.querySelectorAll('[data-b-rail]').forEach((rail) => {
    // Own marker: a feature story is also a rail on phones.
    if (rail.dataset.bRailReady) return;
    rail.dataset.bRailReady = '1';
    const track = rail.querySelector('[data-b-track]');
    const prev = rail.querySelector('[data-b-prev]');
    const next = rail.querySelector('[data-b-next]');
    const bar = rail.querySelector('[data-b-progress]');
    if (!track) return;

    const step = () => {
      const item = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 16;
      return item ? item.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    };
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
      if (bar) bar.style.setProperty('--b-progress', max > 0 ? String(track.scrollLeft / max) : '1');
      rail.classList.toggle('is-static', max <= 2);
    };
    const behavior = () => (reduce.matches ? 'auto' : 'smooth');
    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: behavior() }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: behavior() }));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();

    // Drag with a mouse; touch keeps native swiping.
    let down = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;
    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true;
      moved = false;
      startX = e.clientX;
      startLeft = track.scrollLeft;
      track.classList.add('is-dragging');
    });
    window.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      track.scrollLeft = startLeft - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!down) return;
      down = false;
      track.classList.remove('is-dragging');
    });
    track.addEventListener(
      'click',
      (e) => {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        }
      },
      true
    );
  });
}

/* ---------- Feature story ---------- */

function initStories(scope) {
  scope.querySelectorAll('[data-b-story]').forEach((story) => {
    if (story.dataset.bStoryReady) return;
    story.dataset.bStoryReady = '1';
    const items = [...story.querySelectorAll('[data-b-story-item]')];
    const media = [...story.querySelectorAll('[data-b-story-media]')];
    const count = story.querySelector('[data-b-story-count]');
    if (!items.length) return;
    const activate = (index) => {
      items.forEach((el, i) => el.classList.toggle('is-active', i === index));
      media.forEach((el) => el.classList.toggle('is-active', el.dataset.bStoryMedia === String(index)));
      if (count) count.textContent = String(index + 1).padStart(2, '0');
    };
    activate(0);
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) if (entry.isIntersecting) activate(items.indexOf(entry.target));
      },
      { rootMargin: '-48% 0px -48% 0px', threshold: 0 }
    );
    items.forEach((el) => io.observe(el));
  });
}

/* ---------- In-page bar on model pages ---------- */

function initSubnav(scope) {
  scope.querySelectorAll('[data-b-subnav]').forEach((nav) => {
    if (nav.dataset.bReady) return;
    nav.dataset.bReady = '1';
    const list = nav.querySelector('[data-b-subnav-list]');
    const toggle = nav.querySelector('[data-b-subnav-toggle]');
    const current = nav.querySelector('[data-b-subnav-current]');
    const links = [...nav.querySelectorAll('a[href^="#"]')];
    const targets = links
      .map((a) => document.getElementById(decodeURIComponent(a.getAttribute('href').slice(1))))
      .filter(Boolean);
    // Hide links whose section is not on the page.
    links.forEach((a) => {
      const id = decodeURIComponent(a.getAttribute('href').slice(1));
      if (!document.getElementById(id)) a.closest('li')?.setAttribute('hidden', '');
    });
    const shown = links.filter((a) => !a.closest('li')?.hidden);
    if (current && shown[0]) current.textContent = shown[0].textContent;

    // Compact bar on phones, and wherever the links do not fit in one row.
    const fit = () => {
      if (phone.matches) {
        nav.classList.add('is-compact');
        return;
      }
      nav.classList.remove('is-compact');
      if (list && list.scrollWidth > list.clientWidth + 2) nav.classList.add('is-compact');
      else close();
    };

    // The drop-down list of parts.
    let openedAt = 0;
    // Computers scroll Horizon's page wrapper, phones the window.
    const scrolled = () => window.scrollY + (document.querySelector('.page-wrapper')?.scrollTop || 0);
    const isOpen = () => nav.classList.contains('is-open');
    function close(focus = false) {
      if (!isOpen()) return;
      nav.classList.remove('is-open');
      toggle?.setAttribute('aria-expanded', 'false');
      if (focus) toggle?.focus();
    }
    const open = () => {
      nav.classList.add('is-open');
      toggle?.setAttribute('aria-expanded', 'true');
      openedAt = scrolled();
    };
    toggle?.addEventListener('click', () => (isOpen() ? close() : open()));
    nav.querySelector('[data-b-subnav-close]')?.addEventListener('click', () => close());
    links.forEach((a) => a.addEventListener('click', () => close()));
    nav.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen()) close(true);
    });
    nav.addEventListener('focusout', (e) => {
      if (isOpen() && !nav.contains(e.relatedTarget)) close();
    });
    document.addEventListener('click', (e) => {
      if (isOpen() && !nav.contains(e.target)) close();
    });
    document.addEventListener(
      'scroll',
      () => {
        if (isOpen() && Math.abs(scrolled() - openedAt) > 40) close();
      },
      { capture: true, passive: true }
    );

    fit();
    let frame = 0;
    window.addEventListener(
      'resize',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(fit);
      },
      { passive: true }
    );
    document.fonts?.ready.then(fit);

    const setActive = (id) => {
      links.forEach((a) => {
        const on = a.getAttribute('href') === `#${id}`;
        a.classList.toggle('is-active', on);
        if (on) a.setAttribute('aria-current', 'true');
        else a.removeAttribute('aria-current');
        if (!on) return;
        if (current) current.textContent = a.textContent;
        // In the one-row bar, bring the highlighted link into view.
        if (list && !nav.classList.contains('is-compact')) {
          const left = a.offsetLeft - list.clientWidth / 2 + a.clientWidth / 2;
          list.scrollTo({ left, behavior: reduce.matches ? 'auto' : 'smooth' });
        }
      });
    };
    if (!('IntersectionObserver' in window) || !targets.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) if (entry.isIntersecting) setActive(entry.target.id);
      },
      { rootMargin: '-35% 0px -60% 0px', threshold: 0 }
    );
    targets.forEach((t) => io.observe(t));
    // Show the bar once the opening has scrolled away.
    const hero = document.querySelector('[data-b-subnav-after]');
    if (hero) {
      new IntersectionObserver(([entry]) => nav.classList.toggle('is-shown', !entry.isIntersecting), {
        threshold: 0,
      }).observe(hero);
    } else {
      nav.classList.add('is-shown');
    }
  });
}

/* ---------- Shortened introductions on phones ---------- */

// [data-b-clamp] text longer than a few lines shows the start with a fade and Read more.
function initClamps(scope) {
  scope.querySelectorAll('[data-b-clamp]').forEach((el) => {
    if (el.dataset.bReady) return;
    el.dataset.bReady = '1';
    el.classList.add('b-clamp');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'b-link b-clamp__more';
    button.hidden = true;
    button.setAttribute('aria-expanded', 'false');
    const label = (more) => (more ? 'Read more' : 'Show less');
    button.innerHTML = `<span>${label(true)}</span> <span class="b-link__arrow" aria-hidden="true"></span>`;
    el.after(button);
    let open = false;
    const apply = () => {
      if (!phone.matches) {
        el.classList.remove('is-clamped');
        button.hidden = true;
        return;
      }
      el.classList.toggle('is-clamped', !open);
      // Only offer Read more when the text is longer than the shortened view.
      const long = open || el.scrollHeight > el.clientHeight + 4;
      if (!long) el.classList.remove('is-clamped');
      button.hidden = !long;
    };
    button.addEventListener('click', () => {
      open = !open;
      button.setAttribute('aria-expanded', String(open));
      button.firstElementChild.textContent = label(!open);
      apply();
      if (!open) el.scrollIntoView({ block: 'nearest', behavior: reduce.matches ? 'auto' : 'smooth' });
    });
    phone.addEventListener?.('change', apply);
    apply();
  });
}

/* ---------- Questions and details: smooth open and close ---------- */

function initAccordions(scope) {
  scope.querySelectorAll('details.b-acc__item').forEach((details) => {
    if (details.dataset.bReady) return;
    details.dataset.bReady = '1';
    // A list opened for computers starts closed on phones, where it would fill several screens.
    if (details.hasAttribute('data-b-phone-closed') && phone.matches) details.open = false;
    const summary = details.querySelector('summary');
    const body = details.querySelector('.b-acc__body');
    if (!summary || !body) return;
    summary.addEventListener('click', (e) => {
      if (!motion()) return;
      e.preventDefault();
      if (details.dataset.bAnimating) return;
      details.dataset.bAnimating = '1';
      if (!details.open) {
        details.open = true;
        const h = body.scrollHeight;
        body.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], {
          duration: 420,
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
        }).onfinish = () => delete details.dataset.bAnimating;
      } else {
        const h = body.scrollHeight;
        const anim = body.animate([{ height: `${h}px`, opacity: 1 }, { height: '0px', opacity: 0 }], {
          duration: 320,
          easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
        });
        anim.onfinish = () => {
          details.open = false;
          delete details.dataset.bAnimating;
        };
      }
    });
  });
}

/* ---------- Video ---------- */

function initVideos(scope) {
  scope.querySelectorAll('[data-b-video]').forEach((box) => {
    if (box.dataset.bReady) return;
    box.dataset.bReady = '1';
    box.querySelector('[data-b-play]')?.addEventListener('click', () => {
      const { bType: type, bId: id, bTitle: title } = box.dataset;
      let src = '';
      if (type === 'youtube') src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`;
      if (type === 'vimeo') src = `https://player.vimeo.com/video/${id}?autoplay=1&dnt=1`;
      if (!src) return;
      const frame = document.createElement('iframe');
      frame.src = src;
      frame.title = title || 'Video';
      frame.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
      frame.allowFullscreen = true;
      box.querySelector('[data-b-video-frame]')?.replaceChildren(frame);
      box.classList.add('is-playing');
      frame.focus();
    });
  });
}

/* ---------- List of links: the photo follows the pointer ---------- */

const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

function initIndex(scope) {
  scope.querySelectorAll('[data-b-index]').forEach((list) => {
    if (list.dataset.bReady || !finePointer.matches || !motion()) return;
    list.dataset.bReady = 'true';
    const links = [...list.querySelectorAll('.b-index__link')];
    const float = document.createElement('div');
    float.className = 'b-index-float';
    float.setAttribute('aria-hidden', 'true');
    const images = links.map((link) => {
      const src = link.querySelector('[data-b-index-image]');
      if (!src) return null;
      const img = src.cloneNode();
      img.removeAttribute('loading');
      img.sizes = '380px';
      float.append(img);
      return img;
    });
    if (!images.some(Boolean)) return;
    document.body.append(float);

    let x = 0;
    let y = 0;
    let cx = 0;
    let cy = 0;
    let frame = 0;
    const step = () => {
      cx += (x - cx) * 0.16;
      cy += (y - cy) * 0.16;
      const w = float.offsetWidth;
      const h = float.offsetHeight;
      float.style.transform = `translate3d(${cx - w / 2}px, ${cy - h / 2}px, 0)`;
      frame = Math.abs(x - cx) + Math.abs(y - cy) > 0.5 ? requestAnimationFrame(step) : 0;
    };
    const move = (event) => {
      x = event.clientX;
      y = event.clientY;
      if (!frame) frame = requestAnimationFrame(step);
    };
    links.forEach((link, i) => {
      link.addEventListener('pointerenter', (event) => {
        if (!float.classList.contains('is-on')) {
          cx = x = event.clientX;
          cy = y = event.clientY;
          step();
        }
        images.forEach((img, j) => img?.classList.toggle('is-active', j === i));
        float.classList.toggle('is-on', Boolean(images[i]));
      });
    });
    list.addEventListener('pointermove', move);
    list.addEventListener('pointerleave', () => float.classList.remove('is-on'));
    document.addEventListener('shopify:section:unload', () => float.remove(), { once: true });
  });
}

/* ---------- Builder links: "Continue your build" and the photo glide ---------- */

// Builds are kept in the browser by assets/bossi-builder.js under these keys.
const savedBuild = (handle) => {
  try {
    const build = JSON.parse(window.localStorage.getItem(`bossi:build:${handle}`) || 'null');
    return build && Date.now() - build.at < 60 * 86400000 ? build : null;
  } catch (e) {
    return null;
  }
};
const handleOf = (path) => (String(path).match(/\/products\/([^/?#]+)/) || [])[1] || '';

// The visible words of a link: its first piece of text, leaving out any arrow, icon or text
// that is only there for screen readers.
function wordsNode(link) {
  const walker = document.createTreeWalker(link, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.data.trim() && !node.parentElement.closest('.b-visually-hidden') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP,
  });
  return walker.nextNode();
}

function setWords(link, words) {
  const node = wordsNode(link);
  if (!node) return;
  const lead = node.data.match(/^\s*/)[0];
  const tail = node.data.match(/\s*$/)[0];
  node.data = lead + words + tail;
}

function initBuilderLinks(scope = document) {
  const label = document.querySelector('meta[name="bossi:continue-build"]')?.content || 'Continue your build';
  const currency = window.Shopify?.currency?.active || '';
  scope.querySelectorAll('a[href*="view=builder"]').forEach((link) => {
    let url;
    try {
      url = new URL(link.getAttribute('href'), window.location.href);
    } catch (e) {
      return;
    }
    if (url.searchParams.get('view') !== 'builder') return;
    if (!link.dataset.bBuildHref) {
      link.dataset.bBuildHref = link.getAttribute('href');
      link.dataset.bBuildWords = wordsNode(link)?.data.trim() || '';
    }
    const build = savedBuild(handleOf(url.pathname));
    if (!build) {
      link.setAttribute('href', link.dataset.bBuildHref);
      setWords(link, link.dataset.bBuildWords);
      return;
    }
    link.setAttribute('href', `${url.pathname}?${build.search}`);
    const showPrice = link.classList.contains('b-btn') && !link.classList.contains('b-btn--small');
    const price = showPrice && build.total && (!currency || !build.currency || build.currency === currency) ? ` · ${build.total}` : '';
    setWords(link, label + price);
  });
}

// Going from a bike page to its builder with the opening photo in view: that photo glides into
// the builder (Horizon's page transitions pick up data-view-transition-triggered).
document.addEventListener(
  'click',
  (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href*="view=builder"]') : null;
    if (!link) return;
    // Horizon removes the marker after each page transition, so it is set here, at the click.
    const picture = document.querySelector('.b-model-hero .b-hero__media > picture');
    if (!picture || handleOf(link.pathname) !== handleOf(window.location.pathname)) return;
    const box = picture.getBoundingClientRect();
    const inView = Math.min(box.bottom, window.innerHeight) - Math.max(box.top, 0);
    if (inView < window.innerHeight * 0.5) return;
    picture.setAttribute('data-view-transition-type', 'product-image-transition');
    picture.setAttribute('data-view-transition-triggered', '');
  },
  true
);

// Coming back with the browser's Back button shows the page as it was; refresh the links.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) initBuilderLinks();
});
window.addEventListener('storage', (event) => {
  if (event.key && event.key.startsWith('bossi:build:')) initBuilderLinks();
});

/* ---------- Recent builds: filtering the library by bike ---------- */

function initBuilds(scope) {
  scope.querySelectorAll('[data-b-builds]').forEach((wrap) => {
    if (wrap.dataset.bReady) return;
    wrap.dataset.bReady = '1';
    const chips = [...wrap.querySelectorAll('[data-filter]')];
    const grid = wrap.querySelector('[data-builds-grid]');
    const status = wrap.querySelector('[data-builds-status]');
    const bar = wrap.querySelector('.b-builds__bar');
    if (!grid || !chips.length) return;
    const items = [...grid.children].filter((li) => li.hasAttribute('data-bike'));

    const apply = (key, animate) => {
      const chip = chips.find((c) => c.dataset.filter === key) || chips[0];
      key = chip.dataset.filter;
      chips.forEach((c) => {
        const on = c === chip;
        c.classList.toggle('is-active', on);
        c.setAttribute('aria-pressed', String(on));
      });
      let n = 0;
      items.forEach((li) => {
        const show = !key || li.dataset.bike === key;
        li.hidden = !show;
        if (show) {
          li.style.setProperty('--b-n', String(n));
          li.classList.add('is-in');
          n += 1;
        }
      });
      if (animate && motion()) {
        grid.classList.remove('is-filtered');
        void grid.offsetWidth;
        grid.classList.add('is-filtered');
      }
      if (status) status.textContent = (status.dataset.label || '[count]').replace('[count]', String(n));
      chip.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: animate && motion() ? 'smooth' : 'auto' });
      return key;
    };

    chips.forEach((chip) =>
      chip.addEventListener('click', () => {
        const key = apply(chip.dataset.filter, true);
        const url = new URL(window.location.href);
        if (key) url.searchParams.set('bike', key);
        else url.searchParams.delete('bike');
        history.replaceState(history.state, '', url);
        const top = wrap.getBoundingClientRect().top;
        if (top < 0) wrap.scrollIntoView({ block: 'start', behavior: motion() ? 'smooth' : 'auto' });
      })
    );

    const wanted = new URLSearchParams(window.location.search).get('bike');
    if (wanted) apply(wanted, false);

    // A line under the filters once they stick below the header.
    if (bar) {
      const marker = document.createElement('div');
      marker.setAttribute('aria-hidden', 'true');
      bar.before(marker);
      let queued = false;
      const check = () => {
        queued = false;
        bar.classList.toggle('is-stuck', bar.getBoundingClientRect().top - marker.getBoundingClientRect().top > 2);
      };
      document.addEventListener(
        'scroll',
        () => {
          if (queued) return;
          queued = true;
          requestAnimationFrame(check);
        },
        { capture: true, passive: true }
      );
    }
  });
}

/* ---------- Recent builds: a build's photos ---------- */

function initPostGalleries(scope) {
  scope.querySelectorAll('[data-b-post-gallery].has-many').forEach((gallery) => {
    if (gallery.dataset.bReady) return;
    gallery.dataset.bReady = '1';
    const track = gallery.querySelector('[data-track]');
    const thumbs = [...gallery.querySelectorAll('[data-go]')];
    const thumbList = gallery.querySelector('.b-post-gallery__thumbs');
    const prev = gallery.querySelector('[data-prev]');
    const next = gallery.querySelector('[data-next]');
    const current = gallery.querySelector('[data-current]');
    if (!track) return;
    const count = track.children.length;
    let index = 0;

    const behavior = () => (motion() ? 'smooth' : 'auto');
    const go = (i) => {
      const target = Math.max(0, Math.min(count - 1, i));
      track.scrollTo({ left: target * track.clientWidth, behavior: behavior() });
    };
    const update = () => {
      const i = Math.max(0, Math.min(count - 1, Math.round(track.scrollLeft / Math.max(1, track.clientWidth))));
      if (i === index && current?.textContent === String(i + 1)) return;
      index = i;
      if (current) current.textContent = String(i + 1);
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === count - 1;
      thumbs.forEach((thumb) => {
        const on = Number(thumb.dataset.go) === i;
        if (on) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
        if (on && thumbList) {
          const left = thumb.offsetLeft - (thumbList.clientWidth - thumb.offsetWidth) / 2;
          thumbList.scrollTo({ left: Math.max(0, left), behavior: behavior() });
        }
      });
    };

    let queued = false;
    track.addEventListener(
      'scroll',
      () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          update();
        });
      },
      { passive: true }
    );
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => go(Number(thumb.dataset.go))));
    prev?.addEventListener('click', () => go(index - 1));
    next?.addEventListener('click', () => go(index + 1));
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        go(index + (e.key === 'ArrowRight' ? 1 : -1));
      }
    });
    window.addEventListener('resize', () => go(index), { passive: true });

    // Drag with a mouse; touch keeps native swiping.
    let down = false;
    let startX = 0;
    let startLeft = 0;
    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true;
      startX = e.clientX;
      startLeft = track.scrollLeft;
      track.classList.add('is-dragging');
    });
    window.addEventListener('pointermove', (e) => {
      if (down) track.scrollLeft = startLeft - (e.clientX - startX);
    });
    window.addEventListener('pointerup', (e) => {
      if (!down) return;
      down = false;
      track.classList.remove('is-dragging');
      const moved = e.clientX - startX;
      const from = Math.round(startLeft / Math.max(1, track.clientWidth));
      go(Math.abs(moved) > track.clientWidth * 0.12 ? from + (moved < 0 ? 1 : -1) : from);
    });
    track.addEventListener('dragstart', (e) => e.preventDefault());

    update();
  });
}

/* ---------- Enquiry forms: first and last name also go as "Name" ---------- */

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const full = form.querySelector('[data-fullname]');
    if (!full) return;
    const first = form.querySelector('[data-first]')?.value.trim();
    const last = form.querySelector('[data-last]')?.value.trim();
    full.value = [first, last].filter(Boolean).join(' ');
  },
  true
);

/* ---------- Page position ---------- */

// On computers Horizon scrolls the page inside .page-wrapper and fixes html and body to the
// screen. A jump to something near the end of a page (the footer sign-up after sending it, for
// example) could still nudge html itself, sliding the page up over blank space. Keep it in place.
function keepPageInPlace() {
  // Only while the inner scroller is in use (computers); phones scroll the window normally, and
  // an open menu or cart freezes the page, both of which must be left alone.
  const wrapper = document.querySelector('.page-wrapper');
  if (!wrapper || getComputedStyle(wrapper).overflowY !== 'auto') return;
  for (const el of [document.documentElement, document.body]) {
    if (el && el.scrollTop !== 0 && getComputedStyle(el).overflowY === 'hidden') {
      el.scrollTo({ top: 0, behavior: 'instant' });
    }
  }
}
window.addEventListener('scroll', keepPageInPlace, { passive: true });
window.addEventListener('hashchange', keepPageInPlace);
window.addEventListener('load', keepPageInPlace);
keepPageInPlace();

/* ---------- Start ---------- */

function init(scope = document) {
  initReveal(scope);
  initCounts(scope);
  initRails(scope);
  initStories(scope);
  initSubnav(scope);
  initAccordions(scope);
  initClamps(scope);
  initVideos(scope);
  initIndex(scope);
  initBuilderLinks(scope);
  initBuilds(scope);
  initPostGalleries(scope);
}

init();
root.classList.add('b-motion-ready');

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) init(event.target);
});
